import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import mainLogo from '../../assets/images/mainlogo.png';
import redditLogo from '../../assets/images/icon/Reddit_logo_2023.svg';
import '../../styles/chat-sidebar.css';

// Financial resources
const RESOURCES = [
  { id: 'wsb', name: 'r/wallstreetbets', type: 'reddit', url: 'https://reddit.com/r/wallstreetbets' },
  { id: 'stocks', name: 'r/stocks', type: 'reddit', url: 'https://reddit.com/r/stocks' },
  { id: 'wsj', name: 'Wall Street Journal', type: 'wsj', url: 'https://wsj.com' },
  { id: 'bloomberg', name: 'Bloomberg Markets', type: 'bloomberg', url: 'https://bloomberg.com/markets' },
];

const ChatSidebar = ({
  isOpen,
  isMobile,
  conversations = [],
  currentConversationId,
  onNewConversation,
  onSelectConversation,
  onDeleteConversation,
  onClose
}) => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');

  // Filter conversations based on search
  const filteredConversations = searchQuery
    ? conversations.filter(conv => {
        // Search in multiple fields
        const title = conv.title || '';
        const stocks = (conv.stocksDiscussed || []).join(' ');
        const searchText = `${title} ${stocks}`.toLowerCase();
        return searchText.includes(searchQuery.toLowerCase());
      })
    : conversations; // If no search query, show all conversations

  // Handle conversation click
  const handleConversationClick = (conversationId) => {
    onSelectConversation(conversationId);
    if (isMobile) onClose();
  };

  // Handle new chat
  const handleNewChat = () => {
    onNewConversation();
    if (isMobile) onClose();
  };

  // Handle delete conversation
  const handleDeleteConversation = (conversationId, e) => {
    e.stopPropagation();
    if (window.confirm('Delete this conversation? This action cannot be undone.')) {
      onDeleteConversation && onDeleteConversation(conversationId);
    }
  };

  // Format time with relative dates
  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Handle resource click
  const handleResourceClick = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // Render resource icon
  const renderResourceIcon = (type) => {
    switch (type) {
      case 'reddit':
        return (
          <div className="resource-icon reddit">
            <img src={redditLogo} alt="Reddit" />
          </div>
        );
      case 'wsj':
        return <div className="resource-icon wsj">WSJ</div>;
      case 'bloomberg':
        return <div className="resource-icon bloomberg">B</div>;
      default:
        return <div className="resource-icon">?</div>;
    }
  };

  return (
    <aside
      className={`chat-sidebar ${isMobile ? 'mobile' : 'desktop'} ${isOpen ? 'open' : ''}`}
      aria-label="Chat Navigation"
    >
      {/* Header */}
      <div className="chat-sidebar-header">
        <div className="chat-sidebar-logo">
          <div className="logo-icon-container">
            <img src={mainLogo} alt="Gideon" />
          </div>
          <h2 className="chat-logo-text">GIDEON</h2>
        </div>

        <button
          className="chat-sidebar-close"
          onClick={onClose}
          aria-label="Close sidebar"
        >
          <span className="close-x">×</span>
        </button>
      </div>

      {/* New Chat Button */}
      <button
        className="chat-new-button"
        onClick={handleNewChat}
        aria-label="Start new conversation"
      >
        <span className="chat-new-icon">+</span>
        <span className="chat-new-text">NEW CHAT</span>
      </button>

      {/* Search */}
      <div className="chat-search-container">
        <span className="chat-search-icon-wrapper">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8"/>
            <path d="M21 21l-4.35-4.35"/>
          </svg>
        </span>
        <input
          type="text"
          className="chat-search-input"
          placeholder="Search conversations..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          aria-label="Search conversations"
        />
      </div>

      {/* Scrollable Content Area */}
      <div className="chat-sidebar-content">

        {/* Chat History Section */}
        <div className="chat-history-section">
          {filteredConversations.length === 0 ? (
            <div className="chat-empty-state">
              <p className="chat-empty-text">
                {searchQuery ? 'No conversations found' : 'No recent chats'}
              </p>
              <p className="chat-empty-hint">Start a new chat to begin!</p>
            </div>
          ) : (
            filteredConversations.map((conversation) => (
              <div
                key={conversation.sessionId || conversation.id}
                className={`chat-history-item ${
                  (conversation.sessionId || conversation.id) === currentConversationId ? 'active' : ''
                }`}
              >
                <button
                  className="chat-history-button"
                  onClick={() => handleConversationClick(conversation.sessionId || conversation.id)}
                  aria-label={`View conversation`}
                >
                  <div className="chat-history-content">
                    <div className="chat-history-header">
                      <span className="chat-history-messages">
                        {(conversation.totalMessages || conversation.messageCount || 0) === 0
                          ? 'New conversation'
                          : `${conversation.totalMessages || conversation.messageCount} messages`}
                      </span>
                      <span className="chat-history-time">
                        {formatTime(conversation.lastActivity || conversation.updatedAt)}
                      </span>
                    </div>
                    {conversation.stocksDiscussed && conversation.stocksDiscussed.length > 0 && (
                      <div className="chat-history-stocks">
                        <span className="stocks-label">📊</span>
                        {conversation.stocksDiscussed.slice(0, 3).map(stock => (
                          <span key={stock} className="stock-tag">{stock}</span>
                        ))}
                        {conversation.stocksDiscussed.length > 3 && (
                          <span className="stock-more">+{conversation.stocksDiscussed.length - 3}</span>
                        )}
                      </div>
                    )}
                  </div>
                </button>
                {onDeleteConversation && (
                  <button
                    className="chat-history-delete"
                    onClick={(e) => handleDeleteConversation(conversation.sessionId || conversation.id, e)}
                    aria-label="Delete conversation"
                    title="Delete conversation"
                  >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M4 7h16M9 7V5a1 1 0 011-1h4a1 1 0 011 1v2m-9 0l1 13a1 1 0 001 1h8a1 1 0 001-1l1-13M10 11v6M14 11v6"/></svg>
                  </button>
                )}
              </div>
            ))
          )}
        </div>

        {/* Resources Section */}
        <div className="resources-section">
          <h3 className="resources-title">RESOURCES</h3>
          <div className="resources-list">
            {RESOURCES.map((resource) => (
              <button
                key={resource.id}
                className="resource-item"
                onClick={() => handleResourceClick(resource.url)}
              >
                {renderResourceIcon(resource.type)}
                <span className="resource-name">{resource.name}</span>
              </button>
            ))}
          </div>
        </div>

      </div>

      {/* Footer */}
      <div className="chat-sidebar-footer">
        <div className="footer-buttons">
          <button
            className="footer-btn"
            onClick={() => { navigate('/dashboard'); if (isMobile) onClose(); }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="3"/>
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
            </svg>
            <span>SETTINGS</span>
          </button>
          <button
            className="footer-btn"
            onClick={() => window.open('https://arrowapp.com/help', '_blank')}
          >
            <span className="help-icon">?</span>
            <span>HELP</span>
          </button>
        </div>
        <p className="footer-brand">ArrowApp AI Assistant</p>
      </div>
    </aside>
  );
};

export default ChatSidebar;
